/**
 * MypageReviewFormContainer
 * --
 */
import React from 'react';
import MypageReviewFormPresenter from './MypageReviewFormPresenter';
import { decrypt } from 'utils/Crypto';
import {
  useDefaultShopDataStack,
  useUserDataAction,
  useUserDataStack,
  useUserStack,
} from 'store';
import {
  useCreateReview,
  useDeleteReview,
  useUpdateReview,
} from 'hooks/ReviewHooks';
import {
  useBulkCreateReviewFile,
  useDeleteReviewFile,
} from 'hooks/ReviewFileHooks';
import MessageAlert from 'utils';
import { FileManager } from 'utils/FileManager';
import { useMyShopOrderList } from 'hooks/custom';

const MAX_FILE_COUNT = 5;
const MAX_IMAGE_SIZE = 1 * 1024 * 1024;
const MAX_VIDEO_SIZE = 5 * 1024 * 1024;

const MypageReviewFormContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== Mutate ===== */
  const { createReview } = useCreateReview();
  const { deleteReview } = useDeleteReview();
  const { updateReview } = useUpdateReview();
  const { bulkCreateReviewFile } = useBulkCreateReviewFile();
  const { deleteReviewFile } = useDeleteReviewFile();

  /* ===== PROPS ===== */
  const { searchParams, navigate, screenSize } = props;

  const parcelParams = searchParams.get('orderNum');
  const productParams = searchParams.get('product');
  const reviewType = searchParams.get('type');
  const reviewId = searchParams.get('review');

  // 주문 Parcel idx
  const orderIdx = React.useMemo(() => {
    const decodeIdx = decrypt(parcelParams);

    return Number(decodeIdx);
  }, [parcelParams]);

  // 상품 idx
  const productIdx = React.useMemo(() => {
    const decodeIdx = decrypt(productParams);

    return Number(decodeIdx);
  }, [productParams]);

  // 리뷰 idx
  const reviewIdx = React.useMemo(() => {
    if (!reviewId) return null;

    const decodeIdx = decrypt(reviewId);

    return Number(decodeIdx);
  }, [reviewId]);

  /* ===== Store ===== */
  const userInfo = useUserStack();

  useMyShopOrderList(userInfo.user_id); // 주문내역 데이터 호출 후 스토어 저장 로직 실행

  const { shops } = useDefaultShopDataStack();
  const { orderList, reviews } = useUserDataStack();
  const userDataAction = useUserDataAction();

  /* ===== STATE ===== */
  // 별점 상태 관리
  const [rating, setRating] = React.useState(0);
  // 파일
  const [files, setFiles] = React.useState([]);
  const [inputValue, setInputValue] = React.useState('');
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 리뷰 스코어 핸들러
   * --
   * @param {*} rate
   */
  const handleRating = (rate) => {
    setRating(rate); // 클릭한 별의 위치에 따라 상태 업데이트
  };

  /**
   * 파일 리스트 핸들러
   * --
   * @param {string} type
   * @param {object | number} file
   * @returns
   */
  const handleFileList = (type = 'add', file) => {
    const handlerType = type.toLocaleUpperCase();

    switch (handlerType) {
      case 'ADD':
        const list = Array.from(file);
        if (list.length + files.length > MAX_FILE_COUNT) {
          MessageAlert.warning(
            `파일은 최대 ${MAX_FILE_COUNT}개의 파일만 업로드할 수 있습니다.`
          );
          return;
        }

        // 파일명이 50자 이상 있는지 체크
        const fileNameLength = list.some((file) => file.name.length > 50);
        if (fileNameLength) {
          MessageAlert.info(
            `파일명은 50자를 초과할 수 없습니다. 파일명을 수정해주세요.`
          );
          return;
        }

        // 파일 크기 검사
        const filterFiles = list.filter((file) =>
          file.type.startsWith('image/')
            ? file.size <= MAX_IMAGE_SIZE
            : file.size <= MAX_VIDEO_SIZE
        );

        if (filterFiles.length !== list.length) {
          MessageAlert.info(
            `이미지는 최대 1MB, 동영상은 최대 5MB까지 등록 가능합니다.`
          );
        }

        setFiles((prev) => [...prev, ...filterFiles]);
        return;
      case 'REMOVE':
        setFiles((prev) => [...prev.slice(0, file), ...prev.slice(file + 1)]);
        return;
      default:
        return;
    }
  };

  /**
   * 리뷰 등록
   * --
   */
  const handleSubmit = async (t = 'CREATE') => {
    if (rating < 1) {
      MessageAlert.info('리뷰 점수를 입력해주세요!');
      return;
    }

    if (!inputValue) {
      MessageAlert.info('리뷰 내용을 입력해주세요!');
      return;
    }

    if (inputValue.length < 15) {
      MessageAlert.info('리뷰는 최소 15자 이상 입력해주세요!');
      return;
    }

    const confirm = (msg = '') => window.confirm(msg);

    try {
      setIsLoading(true);

      switch (t) {
        case 'CREATE':
          if (!confirm('리뷰를 등록하시겠습니까?')) return;

          // NOTE: 리뷰 생성 데이터 정제
          const reviewData = {
            user_id: userInfo.user_id,
            [`${reviewType}_id`]: orderIdx,
            product_id: productIdx,
            review_score: rating,
            review_content: inputValue,
          };

          // NOTE: 리뷰 생성
          const { status, data } = await createReview(reviewData);

          let reviewStoreData = {
            ...data,
            review_file: [],
          };

          if (status !== 200) {
            MessageAlert.warning('리뷰 등록에 실패하였습니다.');
            return;
          }

          // NOTE: 파일 등록 처리
          if (files.length) {
            // NOTE: 파일 멀티 업로드
            const upload = await FileManager.multiUpload(
              `/review/${data.review_id}`,
              files
            );

            if (upload.status !== 200) {
              await deleteReview(data.review_id);
              console.error('파일 업로드 실패!', upload);
              MessageAlert.warning('리뷰 등록에 실패하였습니다.');
              return;
            }

            // NOTE: 리뷰 파일 등록
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
            const createData = upload.data.map((file) => ({
              ...file,
              review_id: data.review_id,
              type: imageExtensions.some((ext) =>
                file.file_name.toLowerCase().endsWith(ext)
              )
                ? 'IMAGE'
                : 'VIDEO',
            }));
            const res = await bulkCreateReviewFile(createData);

            if (res.status !== 200) {
              await deleteReview(data.review_id);
              console.error('파일 생성 실패!', upload);
              MessageAlert.warning('리뷰 등록에 실패하였습니다.');
              return;
            }

            reviewStoreData = {
              ...reviewStoreData,
              review_file: res.data,
            };
          }

          userDataAction.addData('reviews', reviewStoreData);
          MessageAlert.success('리뷰가 등록되었습니다!');
          navigate(-1);
          return;

        case 'UPDATE':
          if (!confirm('리뷰를 수정하시겠습니까?')) return;

          // NOTE: 리뷰 생성 데이터 정제
          const updateReviewData = {
            review_id: reviewIdx,
            review_score: rating,
            review_content: inputValue,
          };

          // NOTE: 리뷰 수정
          const { status: updateStatus, data: updateData } = await updateReview(
            updateReviewData
          );

          if (updateStatus !== 200) {
            MessageAlert.warning('리뷰 수정에 실패하였습니다.');
            return;
          }

          // NOTE: 등록/삭제할 파일 분류
          const createFiles = files.filter((file) => !file?.review_file_id);
          const removeFiles = ReviewData?.review_file?.filter(
            (file) =>
              !files.some((f) => f?.review_file_id === file?.review_file_id)
          );
          const remainFiles = ReviewData?.review_file?.filter((file) =>
            files.some((f) => f?.review_file_id === file?.review_file_id)
          );

          let reviewStoreUpdateData = {
            ...updateData,
            review_file: [...remainFiles],
          };

          // NOTE: 파일 등록 처리
          if (createFiles.length) {
            // NOTE: 파일 멀티 업로드
            const updateUpload = await FileManager.multiUpload(
              `/review/${updateData.review_id}`,
              createFiles
            );

            // NOTE: 리뷰 파일 등록
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
            const createData = updateUpload.data.map((file) => ({
              ...file,
              review_id: updateData.review_id,
              type: imageExtensions.some((ext) =>
                file.file_name.toLowerCase().endsWith(ext)
              )
                ? 'IMAGE'
                : 'VIDEO',
            }));
            const updateRes = await bulkCreateReviewFile(createData);

            reviewStoreUpdateData = {
              ...reviewStoreUpdateData,
              review_file: [
                ...reviewStoreUpdateData?.review_file,
                ...updateRes.data,
              ],
            };
          }

          // NOTE: 파일 삭제 처리
          if (removeFiles.length) {
            removeFiles.forEach((file) => {
              deleteReviewFile(file.review_file_id);
            });
          }

          const fid = reviews.findIndex(
            (review) => review.review_id === reviewIdx
          );

          userDataAction.fetchData('reviews', fid, reviewStoreUpdateData);
          MessageAlert.success('리뷰가 등록되었습니다!');
          navigate(-1);

          return;

        default:
          return;
      }
    } catch (err) {
      console.error('[ReviewForm handleSubmit] Error!! ', err);
      MessageAlert.error('리뷰 등록에 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 리뷰 Parcel 데이터 추출
   * --
   */
  const OrderData = React.useMemo(() => {
    if (!orderIdx || !orderList.length || !shops.length) return;

    const findOrder = orderList
      .find((order) =>
        order[reviewType].some((item) => item[`${reviewType}_id`] === orderIdx)
      )
      ?.[reviewType].find((item) => item[`${reviewType}_id`] === orderIdx);

    return {
      ...findOrder,
      shop_information: shops.find(
        (shop) => shop?.shop_information_id === findOrder?.shop_information_id
      ),
    };
  }, [orderIdx, orderList, shops, reviewType]);

  /**
   * 리뷰 수정일 경우 Review 데이터 추출
   * --
   */
  const ReviewData = React.useMemo(() => {
    if (!reviewIdx) return null;

    const review = reviews?.find((review) => review.review_id === reviewIdx);

    return review;
  }, [reviewIdx, reviews]);

  /**
   * 리뷰 수정일 경우 초기값 렌더링
   * --
   */
  React.useEffect(() => {
    if (!ReviewData) return;

    setRating(ReviewData?.review_score);
    setFiles(ReviewData?.review_file);
    setInputValue(ReviewData?.review_content);
  }, [ReviewData]);

  /* ===== RENDER ===== */
  return (
    <>
      <MypageReviewFormPresenter
        order={OrderData}
        rating={rating}
        onRating={handleRating}
        inputValue={inputValue}
        onChange={setInputValue}
        files={files}
        onFileChange={handleFileList}
        maxFileLength={MAX_FILE_COUNT}
        maxImageSize={MAX_IMAGE_SIZE}
        maxVideoSize={MAX_VIDEO_SIZE}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        submitType={ReviewData ? 'UPDATE' : 'CREATE'}
        screenSize={screenSize}
      />
    </>
  );
};

export default MypageReviewFormContainer;
